import React from "react";
import { useTranslation } from "react-i18next";
import {
  AddBoxOutlined,
  AssessmentOutlined,
  BusinessCenterOutlined,
  BusinessOutlined,
  CampaignOutlined,
  CloudSyncOutlined,
  Diversity3Outlined,
  DynamicFormOutlined,
  EventAvailableOutlined,
  GridViewOutlined,
  GroupsOutlined,
  HandshakeOutlined,
  HeadsetMicOutlined,
  HelpCenterOutlined,
  HomeOutlined,
} from "@mui/icons-material";
import { Features, UserRole } from "../../Enums/companyStatus";
import { hasPermission, hasRole } from "../../Helpers/authHelpers";
import { useTerminology } from "../../Core/useTerminology";
import { VOICES_FEATURES } from "../Voices/Navigation/voicesNavigation";
import { useAmbassadorNavigation } from "../Ambassador/Common/ambassadorNavigation";
import { adminRoutes } from "../AdminDashboard/adminRoutes";

export const BOOKING_APP_URL = "https://booking.comostock.app/";
export const ZOHO_CRM_URL =
  "https://accounts.zoho.eu/signin?servicename=ZohoOne&serviceurl=https%3A%2F%2Fone.zoho.eu%2Fzohoone%2Fcorporatemomentum%2Fhome%2Fcxapp%2Fcrm%2Forg20068420939%2Ftab%2FLeads%2Fcustom-view%2F242380000000030939%2Flist&signupurl=https%3A%2F%2Fwww.zoho.eu%2Fone%2Fsignup.html";
export const MICROSOFT_365_URL = "https://www.office.com/";

// Virtual Team Members; the chat page shows its own navigation for this item.
export const VTM_PATH = "/como-experts";

export interface NavItem {
  key: string;
  label: string;
  icon: React.ReactElement;
  path?: string;
  // Link to another app, opened in a new tab.
  externalUrl?: string;
  // Used instead of a path for items that open something, e.g. a modal.
  onClick?: () => void;
  // Set while the page behind the item shows its own navigation in the
  // sidebar: the item then switches back to that navigation instead of
  // navigating.
  drillIn?: () => void;
  badge?: string | null;
  // Extra path prefixes that mark the item as active, e.g. all admin pages.
  activePrefixes?: string[];
  children?: NavItem[];
}

const matchesPrefix = (pathname: string, prefix: string) => {
  const base = prefix.replace(/\/+$/, "");
  return pathname === base || pathname.startsWith(`${base}/`);
};

export const isNavItemActive = (item: NavItem, pathname: string): boolean => {
  if (item.children?.length) {
    return item.children.some((child) => isNavItemActive(child, pathname));
  }
  const prefixes = [item.path, ...(item.activePrefixes ?? [])].filter(
    (prefix): prefix is string => Boolean(prefix),
  );
  return prefixes.some((prefix) => matchesPrefix(pathname, prefix));
};

// First path segment of every admin page, so the Admin Dashboard item stays
// highlighted on /users, /workspace/12, /skill/edit/3 and so on.
const ADMIN_PATH_PREFIXES = Array.from(
  new Set([
    "/user",
    ...adminRoutes
      .flatMap((section) => section.actions)
      .map((action) => action.path)
      .filter((path): path is string => Boolean(path))
      .map((path) => `/${path.split("/")[1]}`),
  ]),
);

export const useNavigationItems = () => {
  const { t } = useTranslation("dashboard");
  const terminology = useTerminology();
  const isAdmin = hasRole(UserRole.ADMIN);
  const can = (feature: Features) => isAdmin || hasPermission(feature);
  const ambassadorNavigation = useAmbassadorNavigation();
  const ambassadorChildren = [
    ...ambassadorNavigation.ambassador,
    ...ambassadorNavigation.como,
  ];

  const auditChildren: NavItem[] = [
    ...(can(Features.EVALUATION)
      ? [
          {
            key: "evaluation",
            path: "/evaluation",
            label: t("dashboard.certificateEvaluation"),
            icon: <AssessmentOutlined />,
          },
        ]
      : []),
    ...(can(Features.QUESTION_LISTS)
      ? [
          {
            key: "createQuestionList",
            path: "/question_list",
            label: t("dashboard.createQuestionList"),
            icon: <AddBoxOutlined />,
          },
          {
            key: "myQuestionLists",
            path: "/questions_list",
            label: t("dashboard.myQuestionLists"),
            icon: <HelpCenterOutlined />,
          },
        ]
      : []),
  ];

  const integrationChildren: NavItem[] = can(Features.ORDER_AN_EXPERT)
    ? [
        {
          key: "orderAnExpert",
          path: "/expert-form",
          label: t("dashboard.orderAnExpert"),
          icon: (
            <img
              src="/alan-icon.png"
              alt=""
              style={{ width: 22, height: 22, objectFit: "contain" }}
            />
          ),
        },
      ]
    : [];

  const main: NavItem[] = [
    {
      key: "home",
      path: "/home",
      label: t("dashboard.home"),
      icon: <HomeOutlined />,
    },
    ...(can(Features.COMO_EXPERTS)
      ? [
          {
            key: "virtualTeamMembers",
            path: VTM_PATH,
            label: t("dashboard.virtualTeamMembers"),
            icon: <GroupsOutlined />,
          },
        ]
      : []),
    ...(isAdmin || VOICES_FEATURES.some(hasPermission)
      ? [
          {
            key: "voices",
            path: "/voices",
            label: t("dashboard.comoVoices"),
            icon: <CampaignOutlined />,
          },
        ]
      : []),
    ...(ambassadorChildren.length
      ? [
          {
            key: "ambassador",
            label: t("dashboard.ambassadorWorkspace"),
            icon: <Diversity3Outlined />,
            children: ambassadorChildren,
          },
        ]
      : []),
    ...(can(Features.ENTITY_CREATION)
      ? [
          {
            key: "entities",
            path: "/entity",
            label: t("dashboard.entities", { entity: terminology.plural }),
            icon: <BusinessOutlined />,
          },
        ]
      : []),
    ...(auditChildren.length
      ? [
          {
            key: "entityAudit",
            label: t("dashboard.entityAudit", {
              entity: terminology.singular,
            }),
            icon: <DynamicFormOutlined />,
            children: auditChildren,
          },
        ]
      : []),
    ...(integrationChildren.length
      ? [
          {
            key: "integrations",
            label: t("dashboard.integrations"),
            icon: <CloudSyncOutlined />,
            children: integrationChildren,
          },
        ]
      : []),
    ...(can(Features.COMO_EXTERNAL_LINKS)
      ? [
          {
            key: "bookingApp",
            externalUrl: BOOKING_APP_URL,
            label: t("dashboard.bookingApp"),
            icon: <EventAvailableOutlined />,
          },
          {
            key: "zohoCrm",
            externalUrl: ZOHO_CRM_URL,
            label: t("dashboard.zohoCrm"),
            icon: <HandshakeOutlined />,
          },
          {
            key: "microsoft365",
            externalUrl: MICROSOFT_365_URL,
            label: t("dashboard.microsoft365"),
            icon: <BusinessCenterOutlined />,
          },
        ]
      : []),
  ];

  const bottom: NavItem[] = [
    ...(isAdmin
      ? [
          {
            key: "adminDashboard",
            path: "/admin-dashboard",
            label: t("dashboard.adminDashboard"),
            icon: <GridViewOutlined />,
            activePrefixes: ADMIN_PATH_PREFIXES,
          },
        ]
      : []),
    {
      key: "help-desk",
      label: t("dashboard.helpDesk"),
      path: "/help-desk",
      icon: <HeadsetMicOutlined />,
    },
  ];

  return { main, bottom };
};
